import { apiBaseUrl } from './baseUrl';
import { retryAfterSeconds } from './retryAfter';

// ─────────────────────────────────────────────────────────────────────
// SIGNING IN AS A PLAYER, WITHOUT A CLUB (#710).
//
// The client for `/auth/otp` — the door that provisions a GLOBAL account for an
// address nobody has seen, whether or not it belongs to a club.
//
// ── WHY NOT `apiFetch` ────────────────────────────────────────────
//
// `apiFetch` attaches whatever credential the current surface holds, and this
// is the request that OBTAINS one. Sending a half-dead session along with a
// sign-in is how a 401 from the old credential ends up reported as a failed
// sign-in — and `clubSession.signIn` uses a raw fetch for the same reason.
//
// `credentials: 'include'` so the browser applies the `Set-Cookie`, which is
// what keeps somebody signed in across a reload (#620). The token is returned
// too, and the two are not alternatives: the cookie is the durable one and the
// bearer takes precedence while the tab lives.
//
// ── IT DOES NOT NAME A CLUB, AND THAT IS THE POINT ────────────────
//
// Every other e-mail door in this app asks for one. The player-first product
// decision is that club membership must not be the prerequisite for having a
// StarPoint identity, so there is nothing to pick before proving who you are.
// ─────────────────────────────────────────────────────────────────────

/** What the API answers on a completed sign-in. */
/** A club the sign-in made the person a member of (owner decision 3A). */
export interface JoinedClub {
  slug: string;
  name: string;
}

export interface AccountSignInSession {
  token: string;
  expiresAt: string;
  /**
   * The clubs whose invitation to this address the sign-in accepted. Empty
   * for most sign-ins; the screen says «Du er nå medlem av X» once per club
   * before it hands the session on. Optional, so the other issuers of this
   * shape (the passkey door, the portal) and their tests need not know.
   */
  joined?: JoinedClub[];
}

/**
 * The outcome, as ONE FLAT SHAPE rather than a tagged union.
 *
 * This repository compiles with `strict: false`, under which a boolean
 * discriminant cannot be narrowed by truthiness — `if (!result.ok)` leaves the
 * union unnarrowed and every field access is an error. `code: string | null`
 * needs no narrowing: null means it worked.
 */
export interface AccountSignInOutcome {
  /** A machine code from the API, or null on success. Never a sentence. */
  code: string | null;
  session: AccountSignInSession | null;
  /**
   * On `too_many_attempts` only: the server's `Retry-After`, in whole seconds,
   * 0 when it could not be read (#778 Q7). The screen shows the real wait and
   * holds the button until it has passed.
   */
  retryAfterSeconds?: number;
}

/** A refusal as the screen needs it: the code, and the wait when it is a throttle. */
function refusal(res: Response, error: unknown): AccountSignInOutcome {
  const code = typeof error === 'string' ? error : 'unknown_error';
  return code === 'too_many_attempts'
    ? { code, session: null, retryAfterSeconds: retryAfterSeconds(res) }
    : { code, session: null };
}

/** Codes this client can produce itself, so a caller never has to guess. */
export const ACCOUNT_SIGN_IN_UNREACHABLE = 'unreachable';
export const ACCOUNT_SIGN_IN_NO_API = 'no_api';

/**
 * Ask for a code.
 *
 * ALWAYS «accepted» for anything the server will act on, found or not — the
 * endpoint answers 202 for an unknown address, a known one and a disabled one
 * alike, because the alternatives are an address oracle and an account oracle.
 * So this reports only whether the REQUEST got through, never anything about
 * the address.
 */
export async function requestAccountCode(email: string): Promise<AccountSignInOutcome> {
  const base = apiBaseUrl();
  if (!base) return { code: ACCOUNT_SIGN_IN_NO_API, session: null };
  let res: Response;
  try {
    res = await fetch(base + '/auth/otp/request', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch {
    // A DROPPED NETWORK IS NOT A REFUSAL. Reported as its own code so the
    // screen can say «we could not reach Starpoint» rather than implying
    // anything about the address.
    return { code: ACCOUNT_SIGN_IN_UNREACHABLE, session: null };
  }
  if (res.ok || res.status === 202) return { code: null, session: null };
  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return refusal(res, body?.error);
}

/**
 * Spend a code.
 *
 * On success the session is RETURNED rather than installed: which holder owns
 * an account session, and which surface the app then renders, is the caller's
 * decision and not this module's. One place decides, and it is not four.
 */
export async function verifyAccountCode(
  email: string,
  code: string,
  /**
   * Ask for the thirty-day personal session (#1091): «Husk meg på denne
   * enheten» in a browser, or the native app on the person's behalf. Absent
   * or false, the ordinary twelve hours — what the club portal always gets.
   */
  remember = false,
): Promise<AccountSignInOutcome> {
  const base = apiBaseUrl();
  if (!base) return { code: ACCOUNT_SIGN_IN_NO_API, session: null };
  let res: Response;
  try {
    res = await fetch(base + '/auth/otp/verify', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, remember }),
    });
  } catch {
    return { code: ACCOUNT_SIGN_IN_UNREACHABLE, session: null };
  }
  const body = (await res.json().catch(() => null)) as {
    token?: unknown;
    expiresAt?: unknown;
    joined?: unknown;
    error?: unknown;
  } | null;

  if (!res.ok) return refusal(res, body?.error);

  // ── A 200 IS NOT A SESSION (#620's lesson, one door along) ──────
  //
  // `String(body.token ?? '')` would turn a malformed answer into a signed-in
  // state holding the empty string — somebody inside the app as nobody, which
  // degrades quietly instead of failing. A response without a usable token is
  // refused.
  const token = typeof body?.token === 'string' ? body.token : '';
  const expiresAt = typeof body?.expiresAt === 'string' ? body.expiresAt : '';
  if (!token || !expiresAt) return { code: 'unknown_error', session: null };

  // A malformed club entry is dropped, not a refusal: the session is real
  // whatever the notice says, and a notice that cannot be read is no notice.
  const joined: JoinedClub[] = Array.isArray(body?.joined)
    ? body.joined.flatMap((raw) => {
        const r = (raw ?? {}) as Record<string, unknown>;
        return typeof r.slug === 'string' && typeof r.name === 'string' && r.name !== ''
          ? [{ slug: r.slug, name: r.name }]
          : [];
      })
    : [];

  // Carried only when there is something to say, so the ordinary sign-in's
  // session is the two-field shape every other issuer of it produces.
  return { code: null, session: { token, expiresAt, ...(joined.length > 0 ? { joined } : {}) } };
}
